import { useCallback, useEffect, useState } from 'react';
import { featureFlagsAPI, studentsAPI } from '../services/api';

export const COMPLETENESS_FLAG = 'students.profile_completeness';

/**
 * Loads completeness stats + paginated student list for the admin page.
 * Gated by students.profile_completeness feature flag.
 */
export default function useStudentProfileCompleteness() {
  const [enabled, setEnabled] = useState(null);
  const [stats, setStats] = useState(null);
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [limit] = useState(50);
  const [status, setStatus] = useState('incomplete');
  const [missingField, setMissingField] = useState(null);
  const [loading, setLoading] = useState(false);
  const [statsLoading, setStatsLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    featureFlagsAPI
      .status([COMPLETENESS_FLAG])
      .then((res) => setEnabled(Boolean(res.data?.data?.enabled?.[COMPLETENESS_FLAG])))
      .catch(() => setEnabled(false));
  }, []);

  const loadStats = useCallback(async () => {
    if (!enabled) return;
    setStatsLoading(true);
    try {
      const res = await studentsAPI.completenessStats();
      setStats(res.data);
    } catch (err) {
      console.error(err);
      if (err.response?.data?.code === 'FEATURE_DISABLED') {
        setEnabled(false);
      } else {
        setError(err.response?.data?.message || 'Failed to load completeness stats');
      }
    } finally {
      setStatsLoading(false);
    }
  }, [enabled]);

  const loadList = useCallback(async () => {
    if (!enabled) return;
    setLoading(true);
    setError('');
    try {
      const params = { page, limit };
      if (missingField) {
        params.missingField = missingField;
        params.status = 'all';
      } else {
        params.status = status;
      }
      const res = await studentsAPI.completenessList(params);
      setItems(res.data?.items || []);
      setTotal(res.data?.total ?? 0);
    } catch (err) {
      console.error(err);
      if (err.response?.data?.code === 'FEATURE_DISABLED') {
        setEnabled(false);
      } else {
        setError(err.response?.data?.message || 'Failed to load students');
      }
      setItems([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [enabled, page, limit, status, missingField]);

  useEffect(() => {
    if (enabled) loadStats();
  }, [enabled, loadStats]);

  useEffect(() => {
    if (enabled) loadList();
  }, [enabled, loadList]);

  const selectMissingField = (key) => {
    setMissingField(key);
    setStatus('incomplete');
    setPage(1);
  };

  const clearMissingField = () => {
    setMissingField(null);
    setPage(1);
  };

  const selectStatus = (next) => {
    setMissingField(null);
    setStatus(next);
    setPage(1);
  };

  const refresh = async () => {
    if (!enabled) return;
    await Promise.all([loadStats(), loadList()]);
  };

  const totalPages = Math.max(1, Math.ceil(total / limit));

  return {
    enabled,
    stats,
    items,
    total,
    page,
    limit,
    totalPages,
    status,
    missingField,
    loading,
    statsLoading,
    error,
    setPage,
    selectMissingField,
    clearMissingField,
    selectStatus,
    refresh,
  };
}
